<template>
  <v-card :class="{ 'floating-mode': isFloating }">
    <h3 v-if="!isFloating" class="small-home-page-card-title pca-red">Unpaid Items</h3>
    <p v-if="!isFloating" class="not-secured-text mb-4 pca-red">Registration is not secured until checkout/payment has been successfully processed.</p>
    <div class="secondary-font summary-content px-4 pt-4 pb-2">
      <!-- Primary entrant -->
      <PaymentSummaryRowItemsByFormType
        :formType="FormType.PRIMARY_ENTRANT"
        :paymentStatus="PaymentStatus.UNPAID"
        :paymentSummary="userDataStore.getPaymentSummary[FormType.PRIMARY_ENTRANT]" 
      />

      <!-- Co-Entrant -->
      <PaymentSummaryRowItemsByFormType
        :formType="FormType.CO_ENTRANT"
        :paymentStatus="PaymentStatus.UNPAID"
        :paymentSummary="userDataStore.getPaymentSummary[FormType.CO_ENTRANT]" 
      />

      <!-- Child -->
      <PaymentSummaryRowItemsByFormType
        :formType="FormType.CHILD"
        :paymentStatus="PaymentStatus.UNPAID"
        :paymentSummary="userDataStore.getPaymentSummary[FormType.CHILD]" 
      />

      <!-- Guest -->
      <PaymentSummaryRowItemsByFormType
        :formType="FormType.GUEST"
        :paymentStatus="PaymentStatus.UNPAID"
        :paymentSummary="userDataStore.getPaymentSummary[FormType.GUEST]" 
      />

      <!-- Event -->
      <PaymentSummaryRowItemsByFormType 
        v-if="new Date(appDataStore.getEventDetails.REGISTRATION_OPEN).getTime() < Date.now()"
        :formType="FormType.EVENT"
        :paymentStatus="PaymentStatus.UNPAID"
        :paymentSummary="getEventPaymentSummary" 
      />

      <!-- Meal -->
      <PaymentSummaryRowItemsByFormType
        v-if="new Date(appDataStore.getEventDetails.REGISTRATION_OPEN).getTime() < Date.now()"
        :formType="FormType.MEAL"
        :paymentStatus="PaymentStatus.UNPAID"
        :paymentSummary="userDataStore.getPaymentSummary[FormType.MEAL]" 
      />

      <!-- Car -->
      <PaymentSummaryRowItemsByFormType
        v-if="new Date(appDataStore.getEventDetails.REGISTRATION_OPEN).getTime() < Date.now()"
        :formType="FormType.CAR"
        :paymentStatus="PaymentStatus.UNPAID"
        :paymentSummary="userDataStore.getPaymentSummary[FormType.CAR]" 
      />

      <!-- Balance due for registered entrants -->
      <div class="d-flex justify-space-between align-center pca-red">
        <div class="mt-3">Balance Due</div>
        <div class="mt-3"><span style="font-size: 20px;">{{ userDataStore.getFormattedBalanceDue }}</span></div>
      </div>

      <div v-if="props.showPayBalanceBtn" class="d-flex justify-end mt-2" style="width: 100%">
        <v-btn
          :disabled="userDataStore.getPrimaryEntrantDetails === null"
          flat
          class="my-2 primary-font"
          color="pca-red"
          style="font-size: 14px; font-weight: 600; height: 32px"
          @click="router.push(RouteName.CHECKOUT)"
        >
          {{ userDataStore.getBalanceDue !== 0 ? 'Pay Balance' : 'Secure Registration' }}
        </v-btn>
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
// Components
import PaymentSummaryRowItemsByFormType from './PaymentSummaryRowItemsByFormType.vue';

// Stores
import { useAppDataStore } from '@/stores/appDataStore';
import { useUserDataStore } from '@/stores/userDataStore';

// Types
import { FormType } from '@/types/user';
import { RouteName } from '@/router/routes';
import { PaymentStatus } from './PaymentSummaryRowItemsByFormType.vue';
import { PaymentSummaryEventItem } from '@/types/user';

// Utils
import { useRouter } from 'vue-router';

// Vue
import { computed, defineComponent, reactive, ref } from 'vue';

export default defineComponent({
  name: 'UnPaidEntrants',
  components: { PaymentSummaryRowItemsByFormType },
  props: {
    showPayBalanceBtn: {
      type: Boolean,
      required: true,
    },
    isFloating: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    // State

    const showDeleteConfirmationDialog = ref<boolean>(false);
    const appDataStore = reactive(useAppDataStore());
    const userDataStore = reactive(useUserDataStore());
    const router = useRouter();

    // Mark the price as 'SOLD OUT' if the event is unpaid and event.seatsAvailable is 0 or negative
    const getEventPaymentSummary = computed((): (Omit<PaymentSummaryEventItem, 'price'> & { price: number | 'SOLD OUT' })[] => {
      return userDataStore.getPaymentSummary[FormType.EVENT].map((event: PaymentSummaryEventItem) => {
        return !event.paid && event.seatsAvailable <= 0 ? { ...event, price: 'SOLD OUT'} : event;
      });
    }); 

    return {
      FormType,
      PaymentStatus,
      RouteName,
      appDataStore,
      getEventPaymentSummary,
      props,
      router,
      showDeleteConfirmationDialog,
      userDataStore,
    };
  },
});
</script>

<style scoped lang="scss">
.v-card {
  border-radius: 20px !important;
  margin-bottom: 35px !important;
  padding: 25px !important;
  max-width: 600px;
  width: 100%;

  &.floating-mode {
    border-radius: 0 !important;
    margin-bottom: 0 !important;
    padding: 0 !important;
    max-width: 100%;
    box-shadow: none !important;
  }
}

.summary-content {
  background-color: $pca-light-grey;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  
  .floating-mode & {
    border-radius: 12px;
  }
}
</style>
